// src/pages/Folder/InstallationDetail.tsx

import {
  AccountBalanceTwoTone,
  ArrowBackIosTwoTone,
  ArrowForwardIosTwoTone,
  AssignmentTwoTone,
  InfoTwoTone,
  MoreVertTwoTone,
  StarBorderTwoTone,
  StarTwoTone,
} from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { memo, useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as Recharts_Tooltip,
  CartesianGrid,
  Brush,
  Legend,
} from 'recharts'

import { SensorRecord, SensorId } from '@/types/domain'
import {
  FolderContractView,
  FolderId,
  FolderName,
  FolderShortName,
} from '@/types/domain/folder'

import {
  addWeekdays,
  categoryByType,
  colorSchemeConsumption,
  colorSchemeFeeling,
  daysAgo,
  daysOnward,
  DDMMYY,
  DDMMYYHHMM,
  DDMMYYYY,
  germanDecimals,
  getDaysArray,
  getHoursArray,
  getMonthsArray,
  getSession,
  getStatusColor,
  installationStatus,
  MMMYY,
  noDecimals,
  normalizedSensorType,
  sensorType,
  sensorUnit,
  setSession,
  SVGStatus,
  userLocales,
  WDDDMMYY,
  WDDDMMYYYYHHMMSS,
} from '../../api'
import {
  AM,
  AMItem,
  DlgInstallationExport,
  HStack,
  KPage,
} from '../../components'
import {
  FolderStatus,
  OperatorData,
  useConsumptionApi,
  useFolderContract,
  useFolderInstallations,
  useFolderName,
  useFolderSensors,
  useFolderShortName,
  useFolderStatus,
  useOperator,
  useSensorPoolIds,
  useTimeSeriesApi,
} from '../../hooks'

const findClosestPoint = (arr, t) => {
  if (!arr || arr.length === 0) return null
  let lo = 0,
    hi = arr.length - 1
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2)
    if (arr[mid].ts === t) return arr[mid]
    if (arr[mid].ts < t) lo = mid + 1
    else hi = mid
  }
  const a = arr[lo],
    b = arr[lo - 1]
  if (!a) return b || null
  if (!b) return a
  return Math.abs(a.ts - t) < Math.abs(b.ts - t) ? a : b
}

type CustomTooltipProps = {
  active?: boolean
  label?: string
  series: any[]
  sensorUnit: (_: string) => string
  feelingType: string
  energyType: string
  userLocales: string
  maxFeeling: number
  maxConsumption: number
  germanDecimals: object
  noDecimals: object
}

const CustomTooltip = memo(
  ({
    active,
    label,
    series,
    sensorUnit,
    feelingType,
    energyType,
    userLocales,
    maxFeeling,
    maxConsumption,
    germanDecimals,
    noDecimals,
  }: CustomTooltipProps) => {
    if (!active || !label) return null
    const ts = Number(label)

    const rows = (series || [])
      .map(s => {
        const pt = findClosestPoint(s.data, ts)
        if (!pt || pt.v === null) return null

        const unit =
          s.axis === 'feeling'
            ? sensorUnit(feelingType)
            : sensorUnit(energyType)

        const formatted = Number(pt.v).toLocaleString(
          userLocales,
          (s.axis === 'feeling' ? maxFeeling : maxConsumption) <= 10
            ? germanDecimals
            : noDecimals
        )

        return (
          <div
            key={`${s.axis}_${s.id}`}
            style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                background: s.color,
                borderRadius: 2,
                marginRight: 6,
              }}
            />
            <span style={{ fontWeight: 500 }}>{s.name}:</span>
            <span>
              <strong>{formatted}</strong> {unit || ''}
            </span>
          </div>
        )
      })
      .filter(Boolean)

    if (rows.length === 0) return null

    return (
      <div
        style={{
          background: 'white',
          border: '1px solid #C8C8C8',
          padding: '10px 12px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
          pointerEvents: 'none',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {new Date(ts).toLocaleString(userLocales, WDDDMMYYYYHHMMSS)}
        </div>
        <div style={{ display: 'grid', gap: 4 }}>{rows}</div>
      </div>
    )
  }
)
CustomTooltip.displayName = 'CustomTooltip'

const isMainSensor = s => !s.parent_id

// wenn Datum in Zukunft, auf heute begrenzen
const limitTodayMax = date =>
  date > new Date(Date.now()).toISOString().split('T')[0]
    ? new Date(Date.now()).toISOString().split('T')[0]
    : date

const granularityCaption = granularity => {
  switch (granularity) {
    case 'd':
      return 'Stündlich'
    case 'm':
      return 'Täglich'
    case 'y':
      return 'Monatlich'
    default:
      console.error('unhandled granularityCaption', granularityCaption)
  }

  return null
}

const getTableRows = row => {
  const data = []

  data.push(
    <TableCell component={'th'} scope={'row'}>
      {row[0]}
    </TableCell>
  )

  for (let i = 1; i < row.length; i++)
    data.push(<TableCell align={'right'}>{row[i]}</TableCell>)

  return data
}

const arrRemove = (arr, value) => arr.filter(f => f !== value)

type Props = {
  PAGE_CONFIG?: string
  period?: string
}

export const InstallationDetail = ({
  PAGE_CONFIG = 'settings_installation_detail',
  period = 'day',
}: Props) => {
  const navigate = useNavigate()

  const theme = useTheme()
  const { white: bgcolor } = theme.palette
  const { main: color } = theme.palette.secondary

  const { id, installationid } = useParams()
  const [searchParams] = useSearchParams()

  const fId: FolderId = Number(id)
  const installationId = Number(installationid)
  const initialSensorId = Number(searchParams.get('sensorId'))

  const fName: FolderName = useFolderName(fId)
  const fShortName: FolderShortName = useFolderShortName(fId)
  const fSensors: SensorRecord[] = useFolderSensors(fId)
  const fStatus: FolderStatus = useFolderStatus(fId)
  const contractData: FolderContractView = useFolderContract(fId)

  const [installation, setInstallation] = useState(installationId)

  const [config, setConfig] = useState(getSession(PAGE_CONFIG))

  const [consumptionSensors, setConsumptionSensors] = useState([])
  const [feelingSensors, setFeelingSensors] = useState([])

  const opData: OperatorData = useOperator()

  const [energyType, setEnergyType] = useState<string>('energy')
  const [feelingType, setFeelingType] = useState<string>('temperature')

  const [selectedInstallationStatus, setSelectedInstallationStatus] =
    useState('green')
  const [showSensorInfo, setShowSensorInfo] = useState(false)

  const sIds: SensorId[] = useSensorPoolIds()

  const [selectedSensors, setSelectedSensors] = useState([])
  const [selectedFeelingSensors, setSelectedFeelingSensors] = useState([])

  const didInitFeelingSensor = useRef(false)
  const didInitSensorFromUrl = useRef(false)

  const [dataEnergy, setDataEnergy] = useState([])
  const [dataFeeling, setDataFeeling] = useState([])

  const [xDomain, setXDomain] = useState(['dataMin', 'dataMax'])

  const [selectedTableSensor, setSelectedTableSensor] = useState({})

  const [maxConsumption, setMaxConsumption] = useState(0)

  const [minFeeling, setMinFeeling] = useState<number>(0)
  const [maxFeeling, setMaxFeeling] = useState(0)

  const [tsDataList, setTsDataList] = useState<{ items: any[] }>({ items: [] })
  const [tsDataListLoading, setTsDataListLoading] = useState(false)

  const [reloadTrigger, setReloadTrigger] = useState(false)

  const [periodWarning, setPeriodWarning] = useState(null)
  const [periodInDays, setPeriodInDays] = useState<number>(0)

  const [exportModal, setExportModal] = useState(false)

  const [actionMenu, setActionMenu] = useState(null)

  const [range, setRange] = useState({
    fromDate: config.fromDate,
    toDate: config.toDate,
    granularity: config.granularity,
  })

  const sensorMapById = useMemo(() => {
    const m = new Map()
    for (const s of fSensors) m.set(Number(s.entity_id), s)
    return m
  }, [fSensors])

  const sensorIndexById = useMemo(() => {
    const m = new Map()
    fSensors.forEach((s, i) => m.set(Number(s.entity_id), i))
    return m
  }, [fSensors])

  const { reBase, reSeries } = useMemo(() => {
    const baseTsSet = new Set()
    const series = []

    const addSeriesFromObj = (obj, ids, axis, palette) => {
      ids.forEach(id => {
        const key = String(id)
        const idx = sensorIndexById.get(Number(id)) ?? 0
        const color = palette[idx % palette.length]
        const name = sensorMapById.get(Number(id))?.name || `Sensor ${id}`

        const pts = (obj[key]?.widget?.[0]?.data || []).map(([t, v]) => {
          const ts = Number(t)
          baseTsSet.add(ts)
          return { ts, v: v === null ? null : Number(v) }
        })

        series.push({ id: key, name, axis, color, data: pts })
      })
    }

    addSeriesFromObj(
      dataFeeling,
      selectedFeelingSensors,
      'feeling',
      colorSchemeFeeling
    )
    addSeriesFromObj(
      dataEnergy,
      selectedSensors,
      'consumption',
      colorSchemeConsumption
    )

    const base = Array.from(baseTsSet)
      .map(Number)
      .sort((a, b) => a - b)
      .map(ts => ({ ts }))

    return { reBase: base, reSeries: series }
  }, [
    dataFeeling,
    dataEnergy,
    selectedFeelingSensors,
    selectedSensors,
    sensorIndexById,
    sensorMapById,
  ])

  const { getFolderConsumption } = useConsumptionApi()

  const [consumptionData, setConsumptionData] = useState([])

  useEffect(() => {
    const fetchConsumption = async () => {
      const data = await getFolderConsumption(
        fId,
        config.granularity,
        config.fromDate + ' 00:00:00',
        config.toDate + ' 23:59:59'
      )
      setConsumptionData(data)
    }

    fetchConsumption()
  }, [
    getFolderConsumption,
    fId,
    config.fromDate,
    config.toDate,
    config.granularity,
  ])

  const { getTimeSeries } = useTimeSeriesApi()

  useEffect(() => setSession(PAGE_CONFIG, config), [PAGE_CONFIG, config])

  const fInstallations = useFolderInstallations(fId)

  useEffect(() => {
    if (fSensors.length === 0) return

    setFeelingSensors(
      fSensors.filter(
        f =>
          f.installation_id === Number(installation) &&
          (categoryByType(f.sensor_type) === 'feeling' ||
            categoryByType(f.sensor_type) === 'security')
      )
    )

    const feelingSensor = fSensors.find(f => f.sensor_type === feelingType)
    if (!feelingSensor) return

    const { name, sensor_type } = feelingSensor

    const status = installationStatus(
      fSensors.filter(f => f.installation_id === Number(installation))
    )

    if (!initialSensorId || !didInitSensorFromUrl.current) {
      setFeelingType(sensor_type)
      setSelectedTableSensor(name)
    }

    setSelectedInstallationStatus(status)
  }, [feelingType, installation, fSensors, initialSensorId])

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      if (!initialSensorId || didInitSensorFromUrl.current) return
      if (fSensors.length === 0) return

      const sensor = fSensors.find(
        f =>
          Number(f.entity_id) === initialSensorId &&
          f.installation_id === Number(installation)
      )

      if (!sensor) return

      didInitSensorFromUrl.current = true
      didInitFeelingSensor.current = true

      const category = categoryByType(sensor.sensor_type)

      setSelectedTableSensor(sensor.name)

      if (category === 'performance') {
        setSelectedSensors([sensor.entity_id])
        setSelectedFeelingSensors([])
        setEnergyType(sensor.type_in_folder)
        return
      }

      if (category === 'feeling' || category === 'security') {
        setSelectedSensors([])
        setSelectedFeelingSensors([sensor.entity_id])
        setFeelingType(sensor.sensor_type)

        setTsDataListLoading(true)
        try {
          const { fromDate, toDate, granularity } = config
          const data = await getTimeSeries(
            sensor.entity_id,
            fromDate + ' 00:00:00',
            toDate + ' 23:59:59',
            granularity
          )
          if (cancelled) return

          setTsDataList(prev => ({
            items: { ...prev.items, [sensor.entity_id]: data },
          }))
        } finally {
          if (!cancelled) setTsDataListLoading(false)
        }
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [initialSensorId, fSensors, installation, config, getTimeSeries])

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      if (feelingSensors.length === 0) return
      if (didInitFeelingSensor.current) return

      const fRecord = feelingSensors.find(
        f => normalizedSensorType(f.sensor_type) === feelingType
      )
      if (!fRecord) return

      didInitFeelingSensor.current = true

      const { entity_id, name } = fRecord

      setSelectedFeelingSensors([entity_id])
      setSelectedTableSensor(name)

      setTsDataListLoading(true)
      try {
        const { fromDate, toDate, granularity } = config
        const data = await getTimeSeries(
          entity_id,
          fromDate + ' 00:00:00',
          toDate + ' 23:59:59',
          granularity
        )
        if (cancelled) return

        setTsDataList(prev => ({
          items: { ...prev.items, [entity_id]: data },
        }))
      } finally {
        if (!cancelled) setTsDataListLoading(false)
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [feelingSensors, feelingType, config, getTimeSeries])

  useEffect(() => {
    const reloadFeelingData = async () => {
      if (!reloadTrigger || selectedFeelingSensors.length === 0) return

      const { fromDate, toDate, granularity } = config
      const newItems = []

      const promises = selectedFeelingSensors.map((id: SensorId) =>
        getTimeSeries(
          id,
          fromDate + ' 00:00:00',
          toDate + ' 23:59:59',
          granularity
        ).then(data => ({ sensorId: id, data }))
      )

      const results = await Promise.all(promises)
      results.forEach(({ sensorId, data }) => {
        newItems[sensorId] = data
      })

      setTsDataList({ items: newItems })
      setReloadTrigger(false)
    }

    reloadFeelingData()
  }, [reloadTrigger, config, selectedFeelingSensors, getTimeSeries])

  useEffect(() => {
    const fromDate = config.fromDate
    const granularity = config.granularity
    const toDate = config.toDate

    const _1H = 60 * 60 * 1000
    const _24H = 24 * _1H

    const periodInDays =
      (new Date(toDate).getTime() - new Date(fromDate).getTime()) / _24H

    setPeriodInDays(periodInDays)

    switch (granularity) {
      case 'd':
        setPeriodWarning(
          periodInDays > 6
            ? 'Diese Granularität ist bei einer Auswahl von bis zu 7 Tagen verfügbar.'
            : null
        )
        break
      case 'm':
        setPeriodWarning(
          periodInDays > 182.625
            ? 'Diese Granularität ist bei einer Auswahl von bis zu 6 Monaten verfügbar.'
            : null
        )
        break
      case 'y':
        setPeriodWarning(null)
        break
      default:
        break
    }
  }, [config.fromDate, config.toDate, config.granularity])

  useEffect(() => {
    if (consumptionData.length === 0) return

    const hasMainSensor = consumptionData
      .filter(f => sIds.includes(f.entity_id))
      .filter(f => f.type_in_folder === energyType)
      .find(f => isMainSensor(f))
    if (!hasMainSensor) return

    const _data = []
    const columnsChart = []
    let _maxConsumption = 0

    consumptionData.forEach(s => {
      const { consumption, entity_id: sId } = s

      if (!selectedSensors.includes(sId)) return

      consumption.values.forEach((item, key) => {
        const { timepoint, value } = item
        const val = Number(value.toFixed(2))

        if (_data[key]) _data[key] = _data[key].concat([val])
        else _data[key] = [timepoint, val]
      })

      if (!columnsChart[sId]) {
        columnsChart[sId] = {
          ui: { min_period: 0, period, max_period: 0 },
          widget: [{ period, type: 'bid', data: [], display: 'absolute' }],
        }
        let minPeriod = 0
        let maxPeriod = 0

        const { ui, widget } = columnsChart[sId]

        consumption.values.forEach(item => {
          const { timepoint, value } = item
          const time = new Date(timepoint).getTime()
          const val = Number(value.toFixed(2))

          if (val > maxPeriod) maxPeriod = val
          else if (val < minPeriod) minPeriod = val

          widget[0].data.push([time, val])
        })

        ui.min_period = minPeriod
        ui.max_period = maxPeriod

        if (maxPeriod > _maxConsumption) _maxConsumption = maxPeriod

        if (widget[0].data.length === 0)
          widget[0].data.push([new Date().getTime(), 0])
      }
    })

    setMaxConsumption(_maxConsumption)
    setConsumptionSensors(consumptionData)
    setDataEnergy(columnsChart)
  }, [
    selectedSensors,
    config.granularity,
    consumptionData,
    energyType,
    sIds,
    period,
    range.granularity,
  ])

  useEffect(() => {
    if (tsDataListLoading) return

    const columnsChart = []

    let avg,
      min,
      max = 0

    if (selectedFeelingSensors.length > 0) min = 1000000

    Object.keys(tsDataList.items).forEach(key => {
      if (selectedFeelingSensors.includes(Number(key)) && !columnsChart[key]) {
        columnsChart[key] = {
          ui: { min_period: 0, period, max_period: 0 },
          widget: [{ period, type: 'bid', data: [], display: 'absolute' }],
        }

        let minPeriod = 10000
        let maxPeriod = -10000

        const { ui, widget } = columnsChart[key]

        const orderedResult = tsDataList.items[key].sort(
          (a, b) => a.timestamp - b.timestamp
        )

        orderedResult.forEach(item => {
          const { timestamp, value } = item
          const val = Number(value.toFixed(2))

          if (val > maxPeriod) maxPeriod = val
          else if (val < minPeriod) minPeriod = val

          avg = avg + val
          widget[0].data.push([timestamp * 1000, val])
        })

        ui.min_period = minPeriod
        ui.max_period = maxPeriod

        if (maxPeriod > max) max = maxPeriod
        if (minPeriod < min) min = minPeriod
      }
    })

    if (min >= 0) min = 0

    setMinFeeling(min)
    setMaxFeeling(max)
    setDataFeeling(columnsChart)
  }, [
    selectedFeelingSensors,
    config.granularity,
    period,
    tsDataList.items,
    tsDataListLoading,
  ])

  const tableDataExportExcel = useMemo(() => {
    let format
    let daylist = []
    const sumArray = [['Summe']]
    const excelArray = []

    switch (config.granularity) {
      case 'd':
        format = DDMMYYHHMM
        daylist = getHoursArray(
          new Date(config.fromDate),
          new Date(config.toDate)
        )
        break
      case 'm':
        format = DDMMYY
        daylist = getDaysArray(
          new Date(config.fromDate),
          new Date(config.toDate)
        )
        break
      case 'y':
        format = MMMYY
        daylist = getMonthsArray(config.fromDate, config.toDate)
        break
      default:
        break
    }

    daylist = daylist.map(day => [day.toLocaleString(userLocales, format)])

    consumptionSensors.forEach(s => {
      const { consumption, entity_id, name, unit } = s
      if (selectedSensors.includes(entity_id)) {
        let sum = 0
        excelArray[name] = []
        daylist.forEach(day => {
          const dayDate = day[0]

          const value = consumption.values.find(f => {
            const valueDate = f.timepoint.slice(0, 10)
            return valueDate === dayDate
          })

          if (value) {
            day.push(`${value.value} ${unit || ''}`)
            sum += value.value

            excelArray[name].push([
              day[0],
              `${value.value.toLocaleString(userLocales, germanDecimals)} ${unit || ''}`,
            ])
          } else {
            day.push(0)
            excelArray[name].push([day[0], `0 ${unit || ''}`])
          }
        })
        const record = `${sum.toLocaleString(userLocales, germanDecimals)} ${
          unit ? unit : ''
        }`
        sumArray[0].push(record)
        excelArray[name].push(['Summe', record])
      }
    })

    const parseDayDate = dateString => {
      if (dateString.includes('-'))
        return new Date(`${dateString.slice(0, 10)}T00:00:00`)

      const parts = dateString.substring(0, 10).split('.')
      return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]))
    }

    Object.keys(tsDataList.items).forEach(s => {
      if (selectedFeelingSensors.includes(Number(s))) {
        let sensorName = ''

        if (fSensors.length > 0)
          sensorName = fSensors.find(f => f.entity_id === Number(s))?.name || s

        excelArray[sensorName] = []

        daylist.forEach(day => {
          const dayDate = parseDayDate(day[0])
          const timestamp = dayDate.getTime() / 1000

          const closest =
            tsDataList.items[s]?.length > 0
              ? tsDataList.items[s].reduce((prev, curr) =>
                  Math.abs(curr.timestamp - timestamp) <
                  Math.abs(prev.timestamp - timestamp)
                    ? curr
                    : prev
                )
              : { value: 0, timestamp }

          const record = `${closest.value.toLocaleString(
            userLocales,
            germanDecimals
          )} ${sensorUnit(feelingType)}`

          day.push(record)
          excelArray[sensorName].push([day[0], record])
        })

        sumArray[0].push('-')
        excelArray[sensorName].push(['Summe', '-'])
      }
    })

    daylist = addWeekdays(daylist, config.granularity)
    Object.keys(excelArray).forEach(data => {
      excelArray[data] = addWeekdays(excelArray[data], config.granularity)
    })

    // Endformat entspricht bisheriger Nutzung im Render
    const result = {}
    result['Korrelation'] = daylist.concat(sumArray)
    // Wir nutzen im Render direkt excelArray (Mapping Name -> Array),
    // genau wie zuvor aus dem State.
    return excelArray
  }, [
    selectedSensors,
    selectedFeelingSensors,
    config.fromDate,
    config.granularity,
    config.toDate,
    consumptionSensors,
    feelingType,
    fSensors,
    tsDataList,
  ])

  useEffect(() => {
    setXDomain(['dataMin', 'dataMax'])
  }, [reBase])

  const changeFeelingSensors = async entity_id => {
    const feelingSensor = fSensors.find(
      f => Number(f.entity_id) === Number(entity_id)
    )
    if (feelingSensor) setSelectedTableSensor(feelingSensor.name)

    const isSelected = selectedFeelingSensors.includes(entity_id)

    if (isSelected) {
      setSelectedFeelingSensors(prev => prev.filter(id => id !== entity_id))
      setTsDataList(prev => {
        const items = { ...prev.items }
        delete items[entity_id]
        return { items }
      })
      return
    }

    setSelectedFeelingSensors(prev => [...prev, entity_id])

    try {
      const { fromDate, toDate, granularity } = config

      const data = await getTimeSeries(
        entity_id,
        fromDate + ' 00:00:00',
        toDate + ' 23:59:59',
        granularity
      )

      setTsDataList(prev => ({
        items: { ...prev.items, [entity_id]: data },
      }))
    } finally {
      setTsDataListLoading(false)
    }
  }

  const changeSelectedSensors = (entity_id, sensorType) => {
    setSelectedSensors(
      selectedSensors.includes(entity_id)
        ? arrRemove(selectedSensors, entity_id)
        : [...selectedSensors, entity_id]
    )
    const consumptionSensor = fSensors?.find(f => f.entity_id === entity_id)
    setSelectedTableSensor(consumptionSensor.name)
    setEnergyType(sensorType)
  }

  const contractBoundaries = date => {
    const date_begin = contractData?.date_begin
    const date_expire = contractData?.date_expire

    if (date < date_begin) return date_begin
    if (date > date_expire) return date_expire
    return date
  }

  const sanitize = date => limitTodayMax(contractBoundaries(date))

  const getSensorById = id =>
    fSensors.find(f => Number(f.entity_id) === Number(id))

  const getTableHeads = () => {
    const data = []
    data.push(<TableCell>Datum</TableCell>)

    selectedSensors.forEach(id => {
      const sensor = getSensorById(id)
      if (sensor.name === selectedTableSensor)
        data.push(<TableCell align={'right'}>{sensor.name}</TableCell>)
    })

    selectedFeelingSensors.forEach(id => {
      const sensor = getSensorById(id)
      if (sensor.name === selectedTableSensor)
        data.push(<TableCell align={'right'}>{sensor.name}</TableCell>)
    })

    return data
  }

  const onChangeInstallation = event => {
    setInstallation(event.target.value)
    setSelectedSensors([])
    setSelectedFeelingSensors([])
    setDataEnergy([])
    setDataFeeling([])
    setTsDataList({ items: [] })
    setSelectedTableSensor({})
    setXDomain(['dataMin', 'dataMax'])
    setMaxConsumption(0)
    setMinFeeling(0)
    setMaxFeeling(0)
    setReloadTrigger(true)
  }

  const onGoBackwardInTime = () => {
    const fromDate = daysAgo(config.fromDate, (periodInDays ?? 0) + 1)
    const toDate = daysAgo(config.toDate, (periodInDays ?? 0) + 1)
    setConfig(prev => ({ ...prev, fromDate, toDate }))
    setRange(prev => ({ ...prev, fromDate, toDate }))
    setReloadTrigger(true)
  }

  const onGoOnwardInTime = () => {
    const fromDate = daysOnward(config.fromDate, (periodInDays ?? 0) + 1)
    const toDate = daysOnward(config.toDate, (periodInDays ?? 0) + 1)
    setConfig(prev => ({ ...prev, fromDate, toDate }))
    setRange(prev => ({ ...prev, fromDate, toDate }))
    setReloadTrigger(true)
  }

  const onChangeFromDate = event => {
    const newRange = {
      ...range,
      fromDate: sanitize(event.target.value),
      ...(sanitize(event.target.value) > range.toDate && {
        toDate: sanitize(event.target.value),
      }),
    }
    setRange(newRange)
  }

  const onChangeToDate = event => {
    const newRange = {
      ...range,
      ...(sanitize(event.target.value) < range.fromDate && {
        fromDate: sanitize(event.target.value),
      }),
      toDate: sanitize(event.target.value),
    }
    setRange(newRange)
  }

  const formatTick = useCallback(
    t => new Date(t).toLocaleString(userLocales, WDDDMMYY),
    []
  )

  const tooltipCursor = useMemo(
    () => ({ stroke: '#a62011', strokeWidth: 1 }),
    []
  )

  return (
    <>
      {/* action menu */}
      <AM
        anchorEl={actionMenu}
        caption={
          (fInstallations.find(i => i.entity_id === installation) as any)?.name
        }
        open={Boolean(actionMenu)}
        onClose={() => setActionMenu(null)}
        historyUrlTarget={'installation'}
        historyUrlId={fId}
      >
        {[
          {
            caption: 'Details zum Gebäude',
            icon: <AccountBalanceTwoTone />,
            fn: () => navigate(`/folder/${fId}`),
            id: 0,
          },
          { caption: null, icon: null, fn: null, id: 1 },
          {
            caption: 'Leistung',
            icon: (
              <SVGStatus type={'performance'} status={fStatus.performance} />
            ),
            fn: () => navigate(`/folder/${fId}/home/performance`),
            id: 2,
          },
          {
            caption: 'Anlagen',
            icon: (
              <SVGStatus type={'installation'} status={fStatus.installation} />
            ),
            fn: () => navigate(`/folder/${fId}/home/installation`),
            id: 3,
          },
          {
            caption: 'Korrelation',
            icon: (
              <SVGStatus type={'correlation'} status={fStatus.correlation} />
            ),
            fn: () => navigate(`/folder/${fId}/home/correlation`),
            id: 4,
          },
          {
            caption: 'Sicherheit',
            icon: <SVGStatus type={'security'} status={fStatus.security} />,
            fn: () => navigate(`/folder/${fId}/home/security`),
            id: 5,
          },
          {
            caption: 'Wohlbefinden',
            icon: <SVGStatus type={'feeling'} status={fStatus.feeling} />,
            fn: () => navigate(`/folder/${fId}/home/feeling`),
            id: 6,
          },
        ].map(item =>
          item.caption !== null ? (
            <AMItem
              caption={item.caption}
              icon={
                <Stack alignItems={'center'} sx={{ width: 25 }}>
                  {item.icon}
                </Stack>
              }
              key={item.id}
              onClick={item.fn}
            />
          ) : (
            <Divider key={item.id} />
          )
        )}
      </AM>
      {/* action menu end */}

      {/* export */}
      <DlgInstallationExport
        open={exportModal}
        onClose={() => setExportModal(false)}
      />
      {/* export end */}

      <KPage
        action={
          <IconButton onClick={event => setActionMenu(event.currentTarget)}>
            <MoreVertTwoTone />
          </IconButton>
        }
        avatar={
          <Avatar>
            <SVGStatus
              type={'installation'}
              status={selectedInstallationStatus}
            />
          </Avatar>
        }
        title={fName + (fShortName ? ` (${fShortName})` : '')}
      >
        {consumptionData.length === 0 ? null : (
          <Card variant={'installation'}>
            <CardHeader
              sx={{ py: 0 }}
              title={
                <HStack
                  justifyContent={'space-between'}
                  sx={{ display: 'flex' }}
                >
                  <HStack alignItems={'center'}>
                    <TextField
                      label={'Anlagen'}
                      onChange={onChangeInstallation}
                      select
                      type={'text'}
                      value={installation}
                      variant={'standard'}
                    >
                      {fInstallations.map(item => (
                        <MenuItem key={item.entity_id} value={item.entity_id}>
                          <HStack>
                            <ListItemIcon>
                              <Box sx={{ width: 25 }}>
                                <SVGStatus
                                  type={'installation'}
                                  status={getStatusColor(item.sensors)}
                                />
                              </Box>
                            </ListItemIcon>

                            <ListItemText>{(item as any).name}</ListItemText>
                          </HStack>
                        </MenuItem>
                      ))}
                    </TextField>
                  </HStack>

                  <HStack alignItems={'center'}>
                    <IconButton
                      disabled={
                        periodWarning ||
                        contractData?.date_begin >= range.fromDate
                      }
                      onClick={onGoBackwardInTime}
                    >
                      <ArrowBackIosTwoTone />
                    </IconButton>

                    <IconButton
                      disabled={
                        periodWarning ||
                        contractData?.date_expire <= range.toDate ||
                        new Date(Date.now()).toISOString().split('T')[0] <=
                          config.toDate
                      }
                      onClick={onGoOnwardInTime}
                    >
                      <ArrowForwardIosTwoTone />
                    </IconButton>

                    <TextField
                      inputProps={{
                        onChange: onChangeFromDate,
                        min: contractData?.date_begin,
                        max: contractData?.date_expire,
                      }}
                      label={'von'}
                      type={'date'}
                      value={range.fromDate}
                      variant={'standard'}
                    />

                    <TextField
                      inputProps={{
                        onChange: onChangeToDate,
                        min: contractData?.date_begin,
                        max: contractData?.date_expire,
                      }}
                      label={'bis'}
                      type={'date'}
                      value={range.toDate}
                      variant={'standard'}
                    />

                    <Button
                      color={'primary'}
                      disabled={
                        config.fromDate === range.fromDate &&
                        config.toDate === range.toDate &&
                        config.granularity === range.granularity
                      }
                      onClick={() => {
                        setReloadTrigger(true)
                        setConfig({
                          ...config,
                          fromDate: range.fromDate,
                          toDate: range.toDate,
                          granularity: range.granularity,
                        })
                      }}
                    >
                      Anwenden
                    </Button>
                  </HStack>
                </HStack>
              }
              variant={'installation'}
            />

            <CardContent sx={{ p: '0px !important' }}>
              <HStack sx={{ bgcolor, alignItems: 'stretch' }}>
                <Stack sx={{ flex: 1, minWidth: 0, my: 0 }}>
                  <ResponsiveContainer width={'100%'} height={500}>
                    <LineChart
                      data={reBase}
                      margin={{ top: 5, right: 20, left: 80, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray={'3 3'} />

                      <XAxis
                        dataKey={'ts'}
                        type={'number'}
                        domain={xDomain}
                        tickFormatter={formatTick}
                        scale={'time'}
                        allowDuplicatedCategory={false}
                        allowDataOverflow
                      />

                      {reSeries.some(s => s.axis === 'feeling') && (
                        <YAxis
                          yAxisId={'feeling'}
                          domain={[
                            Number.isFinite(minFeeling) ? minFeeling : 0,
                            Number.isFinite(maxFeeling)
                              ? maxFeeling
                              : Math.max(1, (minFeeling || 0) + 1),
                          ]}
                          tickFormatter={f =>
                            f.toLocaleString(
                              userLocales,
                              maxFeeling <= 10 ? germanDecimals : noDecimals
                            )
                          }
                          label={{
                            value: `${sensorType(feelingType)} in ${sensorUnit(
                              feelingType
                            )}`,
                            angle: -90,
                            position: 'insideLeft',
                          }}
                        />
                      )}

                      <Legend />

                      {reSeries.some(s => s.axis === 'consumption') && (
                        <YAxis
                          yAxisId={'consumption'}
                          orientation={'right'}
                          domain={[0, Math.max(1, Number(maxConsumption) || 0)]}
                          tickFormatter={f =>
                            f.toLocaleString(
                              userLocales,
                              maxConsumption <= 10 ? germanDecimals : noDecimals
                            )
                          }
                          label={{
                            value: `${sensorType(energyType)} in ${sensorUnit(
                              energyType
                            )}`,
                            angle: -90,
                            position: 'insideRight',
                            offset: 20,
                          }}
                        />
                      )}

                      {reSeries.map(s => (
                        <Line
                          key={`${s.axis}_${s.id}`}
                          yAxisId={s.axis}
                          type={'monotone'}
                          data={s.data}
                          dataKey={'v'}
                          stroke={s.color}
                          dot={false}
                          isAnimationActive={false}
                          connectNulls
                          name={s.name}
                        />
                      ))}

                      <Recharts_Tooltip
                        content={
                          <CustomTooltip
                            series={reSeries}
                            sensorUnit={sensorUnit}
                            feelingType={feelingType}
                            energyType={energyType}
                            userLocales={userLocales}
                            maxFeeling={maxFeeling}
                            maxConsumption={maxConsumption}
                            germanDecimals={germanDecimals}
                            noDecimals={noDecimals}
                          />
                        }
                        isAnimationActive={false}
                        cursor={tooltipCursor}
                        wrapperStyle={{ pointerEvents: 'none' }}
                      />

                      <Brush
                        dataKey={'ts'}
                        height={30}
                        stroke={'#8884d8'}
                        travellerWidth={10}
                        tickFormatter={formatTick}
                        onChange={({ startIndex, endIndex }) => {
                          if (!reBase || reBase.length === 0) return
                          const sIdx = Math.max(
                            0,
                            Math.min(startIndex ?? 0, reBase.length - 1)
                          )
                          const eIdx = Math.max(
                            0,
                            Math.min(
                              endIndex ?? reBase.length - 1,
                              reBase.length - 1
                            )
                          )
                          const startTs = reBase[sIdx]?.ts
                          const endTs = reBase[eIdx]?.ts
                          if (
                            Number.isFinite(startTs) &&
                            Number.isFinite(endTs) &&
                            startTs < endTs
                          )
                            setXDomain([String(startTs), String(endTs)])
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Stack>

                <Stack sx={{ flex: '0 0 300px' }}>
                  <HStack
                    sx={{
                      bgcolor,
                      borderBottom: 'solid 1px #DDD',
                      borderLeft: 'solid 1px #DDD',
                    }}
                  >
                    <Typography
                      sx={{
                        height: 42,
                        lineHeight: 2.5,
                        px: 2,
                        py: 1,
                        width: '75%',
                      }}
                    >
                      Messpunkte
                    </Typography>
                    <Tooltip
                      title={
                        showSensorInfo === true
                          ? 'Informationen zum Messpunkt ausblenden'
                          : 'Informationen zum Messpunkt einblenden'
                      }
                    >
                      <IconButton
                        onClick={() => setShowSensorInfo(!showSensorInfo)}
                      >
                        {showSensorInfo ? (
                          <InfoTwoTone sx={{ color }} />
                        ) : (
                          <InfoTwoTone />
                        )}
                      </IconButton>
                    </Tooltip>
                  </HStack>
                  <List
                    sx={{
                      borderLeft: 'solid 1px #D3D3D3',
                      height: 420,
                      minHeight: 1,
                      maxHeight: 420,
                      overflow: 'auto',
                      width: 300,
                    }}
                  >
                    {fSensors
                      .filter(
                        f =>
                          categoryByType(f.sensor_type) === 'performance' &&
                          f.installation_id === Number(installation)
                      )
                      .map((item, key) => {
                        const {
                          name,
                          entity_id,
                          installation_id,
                          type_in_folder,
                          data_sampling_date: dsd,
                        } = item
                        const date = new Date(dsd).toLocaleDateString(
                          userLocales,
                          DDMMYYYY
                        )

                        const sensorInfo = (
                          <span>
                            {opData?.isAdmin && (
                              <>
                                <span>Sensor ID: {entity_id}</span>
                                <br />
                              </>
                            )}
                            <span>Datenbeginn: {date}</span>
                          </span>
                        )

                        const onClick = () =>
                          changeSelectedSensors(entity_id, type_in_folder)

                        const selected = selectedSensors.includes(entity_id)
                        const color = colorSchemeConsumption[key]
                        const secondary = showSensorInfo ? sensorInfo : null

                        return installation_id === Number(installation) ? (
                          <ListItem key={entity_id} sx={{ p: 0 }}>
                            <ListItemButton
                              onClick={onClick}
                              selected={selected}
                              sx={{ py: 1 }}
                            >
                              <ListItemIcon sx={{ color }}>
                                {isMainSensor(item) ? (
                                  <StarTwoTone />
                                ) : (
                                  <StarBorderTwoTone />
                                )}
                              </ListItemIcon>

                              <ListItemText
                                primary={name}
                                secondary={secondary}
                              />
                            </ListItemButton>
                          </ListItem>
                        ) : (
                          true
                        )
                      })}

                    {fSensors
                      .filter(
                        f =>
                          f.installation_id === Number(installation) &&
                          (categoryByType(f.sensor_type) === 'feeling' ||
                            categoryByType(f.sensor_type) === 'security')
                      )
                      .map((item, key) => {
                        const {
                          name,
                          entity_id,
                          installation_id,
                          data_sampling_date: dsd,
                        } = item

                        const date = new Date(dsd).toLocaleDateString(
                          userLocales,
                          DDMMYYYY
                        )

                        const sensorInfo = (
                          <span>
                            {opData?.isAdmin && (
                              <>
                                <span>Sensor ID: {entity_id}</span>
                                <br />
                              </>
                            )}
                            <span>Datenbeginn: {date}</span>
                          </span>
                        )

                        const onClick = () => changeFeelingSensors(entity_id)

                        const selected =
                          selectedFeelingSensors.includes(entity_id)
                        const color = colorSchemeFeeling[key]
                        const secondary = showSensorInfo ? sensorInfo : null

                        return installation_id === Number(installation) ? (
                          <ListItem key={entity_id} sx={{ p: 0 }}>
                            <ListItemButton
                              onClick={onClick}
                              selected={selected}
                              sx={{ py: 1 }}
                            >
                              <ListItemIcon sx={{ color }}>
                                {isMainSensor(item) ? (
                                  <StarTwoTone />
                                ) : (
                                  <StarBorderTwoTone />
                                )}
                              </ListItemIcon>

                              <ListItemText
                                primary={name}
                                secondary={secondary}
                              />
                            </ListItemButton>
                          </ListItem>
                        ) : (
                          true
                        )
                      })}
                  </List>
                </Stack>
              </HStack>
            </CardContent>
          </Card>
        )}

        {/* table data   */}
        <Collapse in={true}>
          <HStack alignItems={'top'}>
            {Object.keys(tableDataExportExcel).map(item =>
              item === selectedTableSensor ? (
                <TableContainer key={item} sx={{ mt: 2 }}>
                  <Table sx={{ width: 1 }}>
                    <TableHead>
                      <TableRow>{getTableHeads()}</TableRow>
                    </TableHead>

                    <TableBody>
                      {tableDataExportExcel[item].map(row => (
                        <TableRow key={row}>{getTableRows(row)}</TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null
            )}

            <List sx={{ overflow: 'auto', width: 300 }}>
              {Object.keys(tableDataExportExcel).map(item => {
                const onClick = () => setSelectedTableSensor(item)
                const selected = item === selectedTableSensor
                const secondary = granularityCaption(config.granularity)

                return (
                  <ListItem key={item}>
                    <ListItemButton onClick={onClick} selected={selected}>
                      <ListItemIcon>
                        <AssignmentTwoTone />
                      </ListItemIcon>

                      <ListItemText primary={item} secondary={secondary} />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          </HStack>
        </Collapse>
        {/* table data end */}
      </KPage>
    </>
  )
}
